/**
 * Stage history list for a single closed deal.
 *
 * Renders `lead.activities` as a vertical timeline (newest first) of {@link DealCard} rows.
 * Used from `ClosedDealDetails` on the "Stage History" tab. Header matches product timeline pattern
 * (history icon, subtitle, stage count). Each card calls `onDealUpdate` after successful actions.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Clock, History } from 'lucide-react';
import DealCard from './DealCard';
import NoDataFound from '../components/layout/NoDataFound';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

/**
 * @param {object} props
 * @param {number} [props.salesId] Lead id from the route (passed for consistency; list content comes from `dealData`).
 * @param {object} [props.dealData] Closed deal object including `id` and nested `lead.activities`.
 * @param {boolean} props.isLoadingDeals When true, shows a loading state instead of the list.
 * @param {() => void} [props.onDealUpdate] Callback after a stage action succeeds (e.g. refetch closed deals).
 * @param {'default'|'split'} [props.variant] Use `split` when shown beside Logs in a 50/50 row (no outer max-width).
 */
const DealsList = ({ salesId, dealData, isLoadingDeals, onDealUpdate, variant = 'default' }) => {
  const userId = Cookies.get('userId');

  /** Single-deal array mirror of `dealData` for the existing map/filter shape. */
  const [filteredData, setFilteredData] = useState([]);

  /** Closed deal id passed to each `DealCard` for deal-scoped APIs (documents, invoices, loan). */
  const [dealId, setDealId] = useState(null);

  useEffect(() => {
    mixpanel.track('Deals List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  /** Sync local state whenever the parent passes an updated `dealData`. */
  useEffect(() => {
    if (dealData) {
      setFilteredData([dealData]);
      setDealId(dealData?.id);
    }
  }, [dealData]);

  /** Pipeline stages for this lead (from the nested lead on the closed deal). */
  const activities = filteredData?.[0]?.lead?.activities;

  /** Newest activity first (matches header copy and "CURRENT" on latest row). */
  const sortedActivities = useMemo(() => {
    if (!activities?.length) return [];
    return [...activities].sort((a, b) => {
      const ta = new Date(
        a?.updated_at ?? a?.updatedAt ?? a?.created_at ?? a?.createdAt ?? 0
      ).getTime();
      const tb = new Date(
        b?.updated_at ?? b?.updatedAt ?? b?.created_at ?? b?.createdAt ?? 0
      ).getTime();
      const byTime = tb - ta;
      if (byTime !== 0) return byTime;
      return (Number(b?.id) || 0) - (Number(a?.id) || 0);
    });
  }, [activities]);

  const rootClassName =
    variant === 'split'
      ? 'flex flex-col w-full min-w-0 mt-2 overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.04]'
      : 'flex flex-col w-full max-w-[1500px] mx-auto mt-2 rounded-xl border border-gray-200/80 bg-white shadow-sm overflow-hidden';

  if (isLoadingDeals) {
    return (
      <div
        className={
          variant === 'split'
            ? 'flex flex-col items-center justify-center gap-3 min-h-[12rem] w-full min-w-0 mt-2 rounded-2xl border border-slate-200/90 bg-white px-6 py-12 shadow-sm ring-1 ring-slate-900/[0.04]'
            : 'flex flex-col items-center justify-center gap-3 min-h-[12rem] w-full max-w-[1500px] mx-auto mt-2 rounded-xl border border-gray-200 bg-white px-6 py-12'
        }
        role="status"
        aria-live="polite">
        <div className="h-9 w-9 rounded-full border-2 border-[#2A565E]/20 border-t-[#2A565E] animate-spin" />
        <p className="text-sm text-gray-600">Loading stages…</p>
      </div>
    );
  }

  return (
    <div className={rootClassName}>
      <div className="border-b border-slate-100/80 bg-gradient-to-r from-teal-50 via-white to-slate-50 px-4 py-4 sm:px-6 sm:py-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 gap-3">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-700/12 text-teal-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]"
              aria-hidden>
              <History className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight text-slate-900">Stage history</h2>
              <p className="mt-1 max-w-xl text-sm leading-relaxed text-slate-600">
                Newest first — each step shows when the lead moved through your pipeline.
              </p>
            </div>
          </div>
          {sortedActivities.length > 0 ? (
            <div className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200/80 bg-white/90 px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
              <Clock className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
              <span>
                {sortedActivities.length} stage{sortedActivities.length === 1 ? '' : 's'}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {sortedActivities?.length > 0 ? (
        <div className="px-4 py-5 sm:px-6 sm:py-6">
          {/* Spine only spans the card stack (not section padding) and ends at the bottom of the last row. */}
          <div className="relative">
            <div
              className="pointer-events-none absolute bottom-0 left-[22px] top-8 z-0 w-0.5 -translate-x-1/2 bg-[#2A565E]"
              aria-hidden
            />

            <div className="relative z-[1] flex flex-col gap-6 sm:gap-8">
              {sortedActivities.map((item, index) => (
                <DealCard
                  key={item?.id ?? `activity-${index}`}
                  special={index === 0}
                  isLast={index === sortedActivities.length - 1}
                  isPipelineNewest={index === 0}
                  data={item}
                  dealId={dealId}
                  allData={filteredData?.[0]}
                  onSuccess={() => {
                    if (onDealUpdate) {
                      onDealUpdate();
                    }
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="px-4 py-6">
          {/* No activities on the lead — same empty-state pattern as elsewhere in the builder module */}
          <NoDataFound disabledBtn screenHeight={true} />
        </div>
      )}
    </div>
  );
};

export default DealsList;
